"use client"
import { getCentraProduct } from "@/actions/products/get-centra-product-by-uri"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import Loader from "@/app/(site)/_components/Atoms/Loader"
import ColorSwatchesModal from "@/app/(site)/_components/Modules/Cart/QuickShop/ColorSwatchesModal"
import {
    extractProductDetails,
    extractSizeInfo
} from "@/app/(site)/_components/Page/Product/helpers"
import useScrollLock from "@/app/(site)/_utils/_hooks/useScrollLock"
import extractProducts from "@/app/(site)/_utils/transformData/formatProduct"
import useGlobalStore from "@/app/(site)/contexts/ecommerce/store"
import logger from "@/lib/logger/logger-init"
import { convertSnakeCaseToUpperCase } from "@/lib/utils"
import { modalEvent } from "@/lib/utils/tracking/gtmEvents"
import { adaptProductForTracking } from "@/lib/utils/tracking/itemDataTransformer"
import {
    generateProductKeyFromTrackingData,
    storeProductOrigin
} from "@/lib/utils/tracking/productTrackingStore"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { CheckoutAPIProduct } from "@/types/centra/checkout-api/products/post-products"
import { useCallback, useEffect, useRef, useState } from "react"
import QuickShopForm from "../QuickShopForm"
import QuickShopImageGallery from "../QuickShopImageGallery"

type QuickShopModalProps = {
    modalData?: any
    params: { slug: string; locale: string }
    onClose: () => void
    productUri: string
}

export default function QuickShopModal({
    params,
    onClose,
    productUri
}: QuickShopModalProps) {
    const modalRef = useRef<HTMLDivElement>(null)
    const { allowScroll, preventScroll } = useScrollLock()
    const [mainVariant, setMainVariant] = useState<CheckoutAPIProduct | null>(null)
    const [isLoadingColorSwatch, setIsLoadingColorSwatch] = useState(false)
    const [isLoadingMainVariant, setIsLoadingMainVariant] = useState(false)
    const isQuickShopModalOpen = useGlobalStore(state => state.isQuickShopModalOpen)
    const closeQuickShopModal = useGlobalStore(state => state.closeQuickShopModal)
    const [isVisible, setIsVisible] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const siteContext = useSiteContext()

    // Tracking state management
    const hasTrackedOpen = useRef(false)
    const hasTrackedClose = useRef(false)
    const previousModalState = useRef(false)

    const handleClose = () => {
        // Track modal close event only once per cycle
        if (!hasTrackedClose.current) {
            modalEvent("quick-shop-modal", "Quick Shop Modal", "close", siteContext)
            hasTrackedClose.current = true
        }

        setIsVisible(false)
        setMainVariant(null)
        onClose()
        setErrorMessage("")

        setTimeout(() => {
            closeQuickShopModal()
        }, 300)
    }

    const fetchMainVariant = useCallback(async () => {
        setIsLoadingMainVariant(true)
        try {
            const fetchedMainVariant = await getCentraProduct(params, productUri)

            if (fetchedMainVariant) {
                setMainVariant(fetchedMainVariant)
                setIsLoadingMainVariant(false)
            } else {
                logger.warn("Product variant not found.")
                setErrorMessage(
                    "We're sorry, but this variant is currently unavailable. Please try selecting another variant or check back later."
                )

                setIsLoadingMainVariant(false)
            }
        } catch (error) {
            logger.error(error)
            setErrorMessage(
                "We're sorry, but this variant is currently unavailable. Please try selecting another variant or check back later."
            )

            setIsLoadingMainVariant(false)
        } finally {
            setIsLoadingMainVariant(false)
            preventScroll()
        }
    }, [params.locale, preventScroll, productUri])

    useEffect(() => {
        // Only track when modal state actually changes
        if (isQuickShopModalOpen !== previousModalState.current) {
            if (isQuickShopModalOpen && !hasTrackedOpen.current) {
                // Modal just opened - track open event
                modalEvent("quick-shop-modal", "Quick Shop Modal", "open", siteContext)
                hasTrackedOpen.current = true
                hasTrackedClose.current = false // Reset close tracking for this cycle
                setIsVisible(true)
                fetchMainVariant()
            } else if (!isQuickShopModalOpen) {
                // Modal just closed - reset tracking state for next cycle
                hasTrackedOpen.current = false
                hasTrackedClose.current = false
            }

            previousModalState.current = isQuickShopModalOpen
        }

        return () => {
            allowScroll()
        }
    }, [isQuickShopModalOpen, fetchMainVariant, allowScroll, siteContext])

    // Derived data from mainVariant
    const sizes = mainVariant ? extractSizeInfo(mainVariant) : []
    const allRelatedVariants = mainVariant
        ? [mainVariant, ...extractProducts(mainVariant)]
        : []

    // Store product origin when mainVariant is loaded
    useEffect(() => {
        if (mainVariant && isQuickShopModalOpen) {
            const trackingData = adaptProductForTracking(mainVariant, {
                item_list_name: "Quick Shop",
                item_list_id: "quickshop",
                quantity: 1
            })

            const productKey = generateProductKeyFromTrackingData(trackingData)

            storeProductOrigin(
                productKey,
                trackingData.item_list_id || "quickshop",
                trackingData.item_list_name || "Quick Shop",
                "quickshop",
                trackingData.index
            )
        }
    }, [mainVariant, isQuickShopModalOpen])

    const {
        badges,
        productName,
        isProductAvailability,
        selectedFitType,
        fit_type_fit_type
    } = mainVariant
        ? extractProductDetails(mainVariant, allRelatedVariants, undefined)
        : {
              badges: [],
              productName: "",
              isProductAvailability: false,
              selectedFitType: null,
              fit_type_fit_type: ""
          }

    const fit_type_fit_typeName = convertSnakeCaseToUpperCase(selectedFitType || "")

    const handleSwatchSelection = async (swatchUri: string) => {
        setIsLoadingColorSwatch(true)
        try {
            const fetchedNewVariant = await getCentraProduct(params, swatchUri)
            if (!fetchedNewVariant || Array.isArray(fetchedNewVariant)) {
                logger.warn("Product variant not found.")
                return
            }
            setIsLoadingColorSwatch(false)
            setMainVariant(fetchedNewVariant)
        } catch (error) {
            setIsLoadingColorSwatch(false)
            logger.error(error)
        } finally {
            setIsLoadingColorSwatch(false)
        }
    }

    return (
        <div
            className={`fixed inset-0 z-40 flex items-center justify-center bg-black/50 backdrop-blur-md transition-opacity duration-300 ${
                isVisible ? "opacity-100" : "opacity-0"
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quick-shop-modal"
            onClick={handleClose}
        >
            <div
                ref={modalRef}
                className={`grid h-[85vh] w-full max-w-5xl gap-8 rounded-lg bg-main-white p-6 transition-opacity duration-300 md:h-auto md:p-24 ${
                    isVisible ? "scale-100 opacity-100" : "scale-90 opacity-0"
                } grid-rows-2 md:grid-cols-2 md:grid-rows-none`}
                onClick={e => e.stopPropagation()}
            >
                <button
                    className="absolute right-16 top-16 z-30"
                    onClick={handleClose}
                    aria-label="Close Quick Shop Modal"
                >
                    <IconClose />
                </button>
                {errorMessage && !isLoadingMainVariant ? (
                    <span className="col-span-2 flex h-[65vh] items-center justify-start px-24 py-48">
                        {errorMessage}
                    </span>
                ) : mainVariant && !isLoadingMainVariant ? (
                    <>
                        {isLoadingColorSwatch && (
                            <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/30">
                                <Loader />
                            </div>
                        )}

                        <div
                            className={`relative h-full w-full ${isLoadingColorSwatch ? "opacity-50" : "opacity-100"} transition-opacity duration-300`}
                        >
                            <QuickShopImageGallery
                                images={mainVariant?.mediaObjects}
                                badges={Array.isArray(badges) ? badges : []}
                                isProductAvailable={isProductAvailability}
                                prodctName={productName}
                            />
                        </div>
                        <div className="flex h-full w-full justify-between md:flex-col">
                            <QuickShopForm
                                handleClose={handleClose}
                                initialProductValue={mainVariant}
                                relatedVariantsFitType={
                                    Array.isArray(fit_type_fit_type)
                                        ? fit_type_fit_type
                                        : []
                                }
                                productFitType={fit_type_fit_typeName}
                                product={mainVariant}
                                allRelatedVariants={
                                    Array.isArray(allRelatedVariants)
                                        ? allRelatedVariants
                                        : []
                                }
                            >
                                <ColorSwatchesModal
                                    allRelatedVariants={allRelatedVariants}
                                    sizes={sizes}
                                    onSwatchSelect={handleSwatchSelection}
                                    currentProduct={mainVariant}
                                />
                            </QuickShopForm>
                        </div>
                    </>
                ) : (
                    <Loader className="col-span-2 h-[65vh]" />
                )}
            </div>
        </div>
    )
}
